import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Space,
  Popconfirm,
  Select,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  CopyOutlined,
  ReloadOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import moment from 'moment';
import axios from '@app/services/axios';
import {
  getFirstRechargeConfigAction,
  updateFirstRechargeConfigAction,
  getCouponsAction,
  bulkGenerateCouponsAction,
  deleteCouponAction,
} from '@app/store/slices/userSlice';

const { Text } = Typography;
const { Option } = Select;

export const BasicTable: React.FC = () => {
  const dispatch = useAppDispatch();

  // Offer Config State
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configForm] = Form.useForm();
  const [rechargeAmtVal, setRechargeAmtVal] = useState<number>(500);
  const [discountAmtVal, setDiscountAmtVal] = useState<number>(100);

  // Coupons Table State
  const [coupons, setCoupons] = useState<any[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [stats, setStats] = useState({
    totalCoupons: 0,
    availableCoupons: 0,
    usedCoupons: 0,
    redemptionRate: 0,
  });

  // Filter State
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    batch_id: '',
  });

  // Bulk Generator Modal State
  const [genModalOpen, setGenModalOpen] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genForm] = Form.useForm();
  const [previewPrefix, setPreviewPrefix] = useState('VD');

  // Export State
  const [exportLoading, setExportLoading] = useState(false);

  // 1. Fetch First Recharge Config
  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const res: any = await dispatch(getFirstRechargeConfigAction()).unwrap();
      if (res.data) {
        configForm.setFieldsValue({
          enabled: res.data.enabled,
          recharge_amount: res.data.recharge_amount,
          discount_amount: res.data.discount_amount,
          offer_label: res.data.offer_label,
          description: res.data.description,
          terms: res.data.terms,
        });
        setRechargeAmtVal(res.data.recharge_amount || 500);
        setDiscountAmtVal(res.data.discount_amount || 100);
      }
    } catch (err: any) {
      notificationController.error({ message: err || 'Failed to fetch offer configuration' });
    } finally {
      setConfigLoading(false);
    }
  }, [dispatch, configForm]);

  // 2. Fetch Coupons List
  const fetchCoupons = useCallback(async () => {
    setTableLoading(true);
    try {
      const params: any = {
        page: pagination.current,
        limit: pagination.pageSize,
      };
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.batch_id) params.batch_id = filters.batch_id;

      const res: any = await dispatch(getCouponsAction(params)).unwrap();
      if (res.success) {
        setCoupons(res.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.pagination?.total || 0,
        }));
        if (res.stats) {
          setStats(res.stats);
        }
      }
    } catch (err: any) {
      notificationController.error({ message: err || 'Failed to load coupons' });
    } finally {
      setTableLoading(false);
    }
  }, [dispatch, pagination.current, pagination.pageSize, filters]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  // Handle Offer Config Save
  const handleSaveConfig = async () => {
    try {
      const values = await configForm.validateFields();
      setConfigSaving(true);
      await dispatch(updateFirstRechargeConfigAction(values)).unwrap();
      notificationController.success({ message: 'First recharge offer settings updated successfully!' });
      fetchConfig();
    } catch (err: any) {
      if (err?.errorFields) return;
      notificationController.error({ message: err?.message || err || 'Failed to update configuration.' });
    } finally {
      setConfigSaving(false);
    }
  };

  // Handle Bulk Generation
  const handleBulkGenerate = async () => {
    try {
      const values = await genForm.validateFields();
      setGenLoading(true);
      const res: any = await dispatch(bulkGenerateCouponsAction(values)).unwrap();
      if (res.success) {
        notificationController.success({
          message: `Success! ${res.data?.insertedCount || values.count} coupons generated in ${res.data?.durationSeconds || 0}s.`,
        });
        setGenModalOpen(false);
        genForm.resetFields();
        fetchCoupons();
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      notificationController.error({ message: err?.message || err || 'Failed to generate coupons.' });
    } finally {
      setGenLoading(false);
    }
  };

  // Handle Delete Single Coupon
  const handleDeleteCoupon = async (id: string) => {
    try {
      await dispatch(deleteCouponAction(id)).unwrap();
      notificationController.success({ message: 'Coupon deleted successfully.' });
      fetchCoupons();
    } catch (err: any) {
      notificationController.error({ message: err || 'Failed to delete coupon.' });
    }
  };

  // Handle CSV Export
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.batch_id) params.append('batch_id', filters.batch_id);

      const response = await axios.get(`/api/admin/coupons/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `coupons_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      notificationController.success({ message: 'Coupons exported successfully!' });
    } catch (err: any) {
      notificationController.error({ message: 'Failed to export coupons.' });
    } finally {
      setExportLoading(false);
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success(`Copied "${text}" to clipboard!`);
  };

  // Table Columns
  const columns: ColumnsType<any> = [
    {
      title: 'Coupon Code',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => (
        <Space>
          <Text strong style={{ letterSpacing: '1px', fontFamily: 'monospace', fontSize: '14px' }}>
            {code}
          </Text>
          <Button
            size="small"
            type="text"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(code)}
            title="Copy code"
          />
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isUsed',
      key: 'isUsed',
      render: (isUsed: boolean) =>
        isUsed ? (
          <Tag color="volcano">Redeemed</Tag>
        ) : (
          <Tag color="green">Available</Tag>
        ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color="blue">{type || 'first_recharge'}</Tag>,
    },
    {
      title: 'Offer Value',
      key: 'offer_val',
      render: (_: any, record: any) => {
        if (record.isUsed && record.recharge_amount) {
          return (
            <Text>
              ₹{record.recharge_amount} credit (₹{record.discount_amount || 0} off, paid ₹{record.paid_amount || (record.recharge_amount - record.discount_amount)})
            </Text>
          );
        }
        return <Text type="secondary">Follows Config (₹{rechargeAmtVal} for ₹{rechargeAmtVal - discountAmtVal})</Text>;
      },
    },
    {
      title: 'Redeemed By',
      key: 'usedBy',
      render: (_: any, record: any) => {
        if (record.isUsed && record.usedBy) {
          return (
            <div>
              <div><Text strong>{record.usedBy?.name || 'User'}</Text></div>
              <div><Text type="secondary" style={{ fontSize: '12px' }}>{record.usedBy?.phone || ''}</Text></div>
            </div>
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Redeemed At',
      dataIndex: 'usedAt',
      key: 'usedAt',
      render: (usedAt: string) => (usedAt ? moment(usedAt).format('DD MMM YYYY, hh:mm A') : '-'),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => (createdAt ? moment(createdAt).format('DD MMM YYYY, hh:mm A') : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          {!record.isUsed ? (
            <Popconfirm
              title="Are you sure you want to delete this coupon?"
              onConfirm={() => handleDeleteCoupon(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" danger icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          ) : (
            <Text type="secondary" style={{ fontSize: '12px' }}>Locked</Text>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* ─── 1. Offer Settings Card ─────────────────────────────────────── */}
      <Card
        title={
          <Space>
            <SettingOutlined />
            <span>First Recharge Dynamic Offer Settings</span>
          </Space>
        }
        loading={configLoading}
        extra={
          <Button type="primary" onClick={handleSaveConfig} loading={configSaving}>
            Save Offer Settings
          </Button>
        }
      >
        <Form
          form={configForm}
          layout="vertical"
          onValuesChange={(_, all) => {
            if (all.recharge_amount !== undefined) setRechargeAmtVal(Number(all.recharge_amount) || 0);
            if (all.discount_amount !== undefined) setDiscountAmtVal(Number(all.discount_amount) || 0);
          }}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="enabled"
                label="Offer Status"
                valuePropName="checked"
                extra="When disabled, users cannot apply first recharge coupons."
              >
                <Switch checkedChildren="Active" unCheckedChildren="Disabled" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="recharge_amount"
                label="Recharge Credit Amount (₹)"
                rules={[{ required: true, message: 'Please enter base recharge amount' }]}
                extra="Total wallet balance credited to user"
              >
                <InputNumber min={1} max={50000} style={{ width: '100%' }} prefix="₹" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="discount_amount"
                label="Discount / Off Amount (₹)"
                rules={[{ required: true, message: 'Please enter discount amount' }]}
                extra="Instant off given on checkout"
              >
                <InputNumber min={1} max={50000} style={{ width: '100%' }} prefix="₹" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ padding: '12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', marginTop: '4px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>Customer Net Payable</Text>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a' }}>
                  ₹{Math.max(0, rechargeAmtVal - discountAmtVal)}
                </div>
                <Text style={{ fontSize: '11px', color: '#389e0d' }}>
                  Pays ₹{Math.max(0, rechargeAmtVal - discountAmtVal)} to get ₹{rechargeAmtVal} wallet credit!
                </Text>
              </div>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="offer_label"
                label="Offer Display Label"
                rules={[{ required: true, message: 'Please enter offer label' }]}
              >
                <Input placeholder="e.g. First Recharge Special Offer" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item name="description" label="Offer Description">
                <Input placeholder="e.g. Special introductory offer on your first wallet recharge!" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item name="terms" label="Terms & Conditions Note">
                <Input placeholder="e.g. Valid only for first-time wallet recharges with a valid coupon code." />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* ─── 2. KPI Summary Cards ────────────────────────────────────────── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Coupons Generated"
              value={stats.totalCoupons}
              prefix={<GiftOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Available / Unused"
              value={stats.availableCoupons}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Redeemed by Users"
              value={stats.usedCoupons}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Redemption Rate"
              value={stats.redemptionRate}
              suffix="%"
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ─── 3. Coupons Inventory Table & Actions ───────────────────────── */}
      <Card
        title="Coupons Inventory"
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              loading={exportLoading}
            >
              Export to CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                genForm.setFieldsValue({ count: 1000, prefix: 'VD', length: 8 });
                setPreviewPrefix('VD');
                setGenModalOpen(true);
              }}
            >
              Bulk Generate Coupons
            </Button>
          </Space>
        }
      >
        {/* Filters Bar */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search coupon code..."
              allowClear
              value={filters.search}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, search: e.target.value }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by Status"
              allowClear
              value={filters.status || undefined}
              onChange={(val) => {
                setFilters((prev) => ({ ...prev, status: val || '' }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            >
              <Option value="">All Statuses</Option>
              <Option value="available">Available (Unused)</Option>
              <Option value="used">Redeemed (Used)</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Filter by Batch ID..."
              allowClear
              value={filters.batch_id}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, batch_id: e.target.value }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button icon={<ReloadOutlined />} onClick={() => fetchCoupons()}>
              Refresh
            </Button>
          </Col>
        </Row>

        <BaseTable
          loading={tableLoading}
          columns={columns}
          dataSource={coupons}
          rowKey="_id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize, total: pagination.total });
            },
          }}
        />
      </Card>

      {/* ─── 4. Bulk Generate Modal ──────────────────────────────────────── */}
      <Modal
        title="Bulk Generate Coupon Codes"
        open={genModalOpen}
        onCancel={() => setGenModalOpen(false)}
        onOk={handleBulkGenerate}
        confirmLoading={genLoading}
        okText="Generate Coupons"
      >
        <Form form={genForm} layout="vertical">
          <Form.Item
            name="count"
            label="Number of Coupons to Generate"
            rules={[
              { required: true, message: 'Please specify count' },
              {
                type: 'number',
                min: 1,
                max: 50000,
                message: 'Must be between 1 and 50,000',
              },
            ]}
            extra="You can generate up to 20,000+ coupons in a single batch."
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={50000}
              step={1000}
              placeholder="e.g. 20000"
            />
          </Form.Item>

          <Form.Item
            name="prefix"
            label="Coupon Prefix"
            rules={[{ required: true, message: 'Please enter a prefix' }]}
            extra="Example: VD, WELCOME, MILK, FIRST"
          >
            <Input
              placeholder="e.g. VD"
              maxLength={10}
              onChange={(e) => setPreviewPrefix(e.target.value.toUpperCase())}
            />
          </Form.Item>

          <Form.Item
            name="length"
            label="Random Character Length"
            rules={[{ required: true, message: 'Please select code length' }]}
          >
            <InputNumber style={{ width: '100%' }} min={4} max={16} defaultValue={8} />
          </Form.Item>

          <div
            style={{
              padding: '12px',
              background: '#f5f5f5',
              borderRadius: '6px',
              textAlign: 'center',
            }}
          >
            <Text type="secondary" style={{ fontSize: '12px' }}>Code Sample Preview:</Text>
            <div style={{ fontSize: '16px', fontWeight: 'bold', letterSpacing: '1px', color: '#1890ff', marginTop: '4px' }}>
              {previewPrefix ? `${previewPrefix}-8X9J2K1M` : '8X9J2K1M'}
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
